import { useInternationalization } from '@progress/kendo-react-intl';
import { useCallback, useMemo } from 'react';
import Layout from 'src/components/Layout';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import useRedirect from 'src/hooks/useRedirect';
import useViewBudgetPeriod from 'src/hooks/useViewBudgetPeriod';
import useMultiPeriodAllocationUpdateApi from 'src/services/useMultiPeriodAllocationUpdateApi';

const ViewBudgetPeriodActions = () => {
  const { getUrl, redirect } = useRedirect();
  const {
    poId,
    poDetails,
    budgetPeriods,
    refreshBudgetPeriods,
    saveErrorList,
    errorMessageList,
    warningList,
    hasErrors,
    hasWarnings,
    isReadOnly,
    isBudgetPeriodDirty,
    t,
  } = useViewBudgetPeriod();
  const { updateMultiPeriodAllocation } = useMultiPeriodAllocationUpdateApi(poId);
  const intl = useInternationalization();

  const handleCancel = useCallback(() => {
    redirect(getUrl('poDetails')(poId), true, false);
  }, [getUrl, poId, redirect]);

  const handleSave = useCallback(() => {
    const multiPeriodList = budgetPeriods.map((item) => ({
      glPeriodId: item.glPeriodId,
      amount: item.amount,
    }));
    updateMultiPeriodAllocation(multiPeriodList).then((res) => {
      if (res.success) {
        refreshBudgetPeriods().then(() => {
          setTimeout(() => {
            handleCancel();
          }, 2000);
        });
      }
    });
  }, [budgetPeriods, handleCancel, refreshBudgetPeriods, updateMultiPeriodAllocation]);

  const { errorMessage, isSaveDisabled, showErrorPopUp } = useMemo(() => {
    const errorMessage = [...saveErrorList, ...errorMessageList];
    const isSaveDisabled = hasErrors || !isBudgetPeriodDirty;
    const showErrorPopUp = hasErrors || hasWarnings;

    return { errorMessage, isSaveDisabled, showErrorPopUp };
  }, [errorMessageList, hasErrors, hasWarnings, isBudgetPeriodDirty, saveErrorList]);
  return (
    <Layout>
      <section className="h-14 shadow-md flex items-center gap-4">
        {!isReadOnly ? (
          <div className="flex items-center">
            <Button onClick={handleCancel} color="error">
              {t('Cancel')}
            </Button>
            <PopUp
              popupAlign={{ horizontal: 'center', vertical: 'top' }}
              content={
                <div className="m-1 max-w-[350px]">
                  <PopUpContentWrapper type="Error" contents={errorMessage} />
                  <PopUpContentWrapper type="Warning" contents={[...warningList]} />
                </div>
              }
              show={showErrorPopUp}
            >
              <Button onClick={handleSave} disabled={isSaveDisabled}>
                {t('Save')}
              </Button>
            </PopUp>
          </div>
        ) : (
          <div className="flex items-center">
            <Button onClick={handleCancel} color="info">
              {t('Go Back')}
            </Button>
          </div>
        )}
        <p className="font-bold font-md">{t('Manage Accounting Period Allocations')}</p>
      </section>
      <section>
        <div className="bg-[#F6F6F6] h-11 text-xs flex items-center font-medium gap-4">
          <span>
            {t('PO')} #: <span className="font-bold">{poDetails?.poNum}</span>
          </span>
          <span className="text-[#AAAAAA]"> | </span>
          <span>
            {t('Buyer')}: <span className="font-bold">{poDetails?.buyerName}</span>
          </span>
          <span className="text-[#AAAAAA]"> | </span>
          <span>
            {t('Supplier')}: <span className="font-bold">{poDetails?.supplierName}</span>
          </span>
          <span className="text-[#AAAAAA]"> | </span>
          <span>
            {t('Total Amount')}:
            <span className={`${errorMessageList.length > 0 ? 'text-error' : ''} font-bold`}>
              {intl.formatNumber(poDetails?.poTotal || 0, 'c2')}
            </span>
          </span>
        </div>
      </section>
    </Layout>
  );
};

export default ViewBudgetPeriodActions;
